import { Navigate, Route, Routes } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from './app/AuthProvider';
import { AppLayout } from './layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { PaymentMethodsPage } from './pages/PaymentMethodsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { ServicePlansPage } from './pages/ServicePlansPage';
import { MikrotikPage } from './pages/MikrotikPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PlatformPage } from './pages/PlatformPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { FirstLoginPasswordPage } from './pages/FirstLoginPasswordPage';

function FullPageSpinner() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoutes() {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <AppLayout />;
}

function RequiredPasswordChangeRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.mustChangePassword) return <RoleLanding />;
  return <FirstLoginPasswordPage />;
}

function PublicOnlyRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (isAuthenticated) return <RoleLanding />;
  return children;
}

function RoleLanding() {
  const { user } = useAuth();
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  const destination = user?.role === 'SUPER_ADMIN'
    ? '/platform'
    : user?.role === 'VIEWER' || user?.role === 'SUPPORT'
      ? '/clients'
      : '/dashboard';
  return <Navigate to={destination} replace />;
}
function RoleRoute({ roles, children }: { roles: string[]; children: React.ReactElement }) {
  const { user } = useAuth();
  return user && roles.includes(user.role) ? children : <RoleLanding />;
}
function SuperAdminRoute({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  return user?.role === 'SUPER_ADMIN' ? children : <Navigate to="/dashboard" replace />;
}
export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicOnlyRoute>
            <ResetPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route path="/change-password" element={<RequiredPasswordChangeRoute />} />

      <Route element={<ProtectedRoutes />}>
        <Route index element={<RoleLanding />} />
        <Route path="/platform" element={<SuperAdminRoute><PlatformPage /></SuperAdminRoute>} />
        <Route path="/dashboard" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER', 'BILLING']}><DashboardPage /></RoleRoute>} />
        <Route path="/clients" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER', 'BILLING', 'VIEWER', 'SUPPORT']}><ClientsPage /></RoleRoute>} />
        <Route path="/clients/:id" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER', 'BILLING', 'VIEWER', 'SUPPORT']}><ClientDetailPage /></RoleRoute>} />
        <Route path="/payments" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER', 'BILLING']}><PaymentsPage /></RoleRoute>} />
        <Route path="/payment-methods" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER']}><PaymentMethodsPage /></RoleRoute>} />
        <Route path="/invoices" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER', 'BILLING']}><InvoicesPage /></RoleRoute>} />
        <Route path="/service-plans" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER']}><ServicePlansPage /></RoleRoute>} />
        <Route path="/mikrotik" element={<RoleRoute roles={['ORG_ADMIN', 'MANAGER']}><MikrotikPage /></RoleRoute>} />
        <Route path="/settings" element={<RoleRoute roles={['ORG_ADMIN']}><SettingsPage /></RoleRoute>} />
        <Route path="/users" element={<RoleRoute roles={['ORG_ADMIN']}><UsersPage /></RoleRoute>} />
        <Route path="/audit-logs" element={<RoleRoute roles={['SUPER_ADMIN', 'ORG_ADMIN']}><AuditLogsPage /></RoleRoute>} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}





